import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialog";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHook";
import useToast from "../../../hooks/useToast";
import {
  fetchGetMe,
  fetchPlayerTroop,
} from "../../../redux/pages/barracks/barracksActions";
import { PlayerTroopType } from "../../../types/troop";
import Network from "../../../utils/Network";
import { roundFloat } from "../../../utils/round-float";
import ThousandsSeparator from "../../../utils/thousands-separator";
import { useBarracksUIContext } from "../BarracksContext";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

// What the server answers for a cost preview and for a heal. Everything after troopsToHeal is
// newer; an older server leaves it out and the dialog falls back to the CLEG price alone.
type InstantHealPreview = {
  requiredHpAmount: number;
  selectedPercentage: number;
  finalCostInCleg: number;
  equivalentCosts: Record<string, number>;
  troopsToHeal: InstantHealTroopEntry[];
  troopCount?: number;
  baseCostInCleg?: number;
  discountPercent?: number;
  discountAmountInCleg?: number;
  promotion?: InstantHealAppliedPromotion;
  availablePromotions?: InstantHealPromotionInfo[];
  paymentOptions?: InstantHealPaymentOption[];
};

type InstantHealAppliedPromotion = {
  id: number;
  name: string;
  description?: string;
  ruleId: number;
  discountPercent: number;
};

type InstantHealPromotionRule = {
  id: number;
  conditionType: number;
  conditionValue: number;
  discountPercent: number;
};

type InstantHealPromotionInfo = {
  id: number;
  name: string;
  description?: string;
  rules?: InstantHealPromotionRule[];
};

type InstantHealPaymentOption = {
  paymentType: number;
  currency: string;
  amount: number;
  priceInCleg: number;
  balance?: number;
  isAvailable: boolean;
  canAfford: boolean;
};

type InstantHealTroopEntry = {
  id?: number;
  troopId?: number;
  playerTroopId?: number;
  name?: string;
  troopName?: string;
  health?: number;
  maxHealth?: number;
  currentHealth?: number;
  image?: string;
  icon?: string;
};

const percentageOptions = [25, 50, 100];

const getTroopEntryId = (entry: InstantHealTroopEntry) => {
  return entry?.playerTroopId ?? entry?.troopId ?? entry?.id ?? 0;
};

// The currencies a heal can be paid with, by the server's paymentType number.
const CLEG_PAYMENT = 1;
const TROOP_COUNT_CONDITION = 1;
const currencies: Record<number, { key: "cleg" | "iron" | "stone"; icon: string }> = {
  1: { key: "cleg", icon: "/assets/images/icons/cleg.png" },
  4: { key: "iron", icon: "/assets/images/icons/metal.png" },
  5: { key: "stone", icon: "/assets/images/icons/stone.png" },
};
const clegIcon = currencies[CLEG_PAYMENT].icon;

const isPayable = (option: InstantHealPaymentOption) =>
  option.isAvailable && option.canAfford;

const formatValue = (value?: number) => {
  if (value === null || value === undefined) return "--";
  return ThousandsSeparator(roundFloat(value));
};

// Prices are exact to 4 decimals on the server; show what will be charged.
const formatCost = (value?: number) => {
  if (value === null || value === undefined) return "--";
  return ThousandsSeparator(roundFloat(value, 4));
};

const renderSkeletonLine = (width: number | string) => (
  <Skeleton width={width} height={16} />
);

const renderSkeletonBlock = (height: number) => (
  <Skeleton height={height} />
);

const InstantHealDialog = () => {
  const { t } = useAppTranslation(["barracks", "common"]);
  const axios = useMemo(() => new Network(), []);
  const openToast = useToast();
  const dispatch = useAppDispatch();

  const barracksContext = useBarracksUIContext();
  const barracksUIProps = useMemo(() => {
    return {
      instantHealDialog: barracksContext?.instantHealDialog,
      setInstantHealDialog: barracksContext?.setInstantHealDialog,
    };
  }, [barracksContext?.instantHealDialog, barracksContext?.setInstantHealDialog]);

  const playerTroopList = useAppSelector(
    ({ barracks }: any) => barracks.playerTroopList
  ) as PlayerTroopType[];

  const [percentage, setPercentage] = useState<number>(100);
  const [preview, setPreview] = useState<InstantHealPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [troopsSkeletonCount, setTroopsSkeletonCount] = useState(3);
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    number | null
  >(null);
  // Bumped to ask for the price again, after a refused heal.
  const [refreshKey, setRefreshKey] = useState(0);

  const open = !!barracksUIProps?.instantHealDialog?.open;
  const troopId = barracksUIProps?.instantHealDialog?.troopId ?? 0;
  const targetType =
    barracksUIProps?.instantHealDialog?.targetType ??
    (troopId > 0 ? "single" : "all");

  const activeTroop = useMemo(() => {
    if (barracksUIProps?.instantHealDialog?.troop) {
      return barracksUIProps?.instantHealDialog?.troop;
    }
    if (!troopId) return undefined;
    return playerTroopList?.find((troop) => troop.id === troopId);
  }, [barracksUIProps?.instantHealDialog?.troop, troopId, playerTroopList]);

  const paymentOptions = useMemo<InstantHealPaymentOption[]>(() => {
    if (!preview) return [];
    if (preview.paymentOptions?.length) {
      return preview.paymentOptions.filter(
        (option) => !!currencies[option.paymentType]
      );
    }
    if (typeof preview.finalCostInCleg === "number" && preview.finalCostInCleg > 0) {
      return [
        {
          paymentType: CLEG_PAYMENT,
          currency: "CLEG",
          amount: preview.finalCostInCleg,
          priceInCleg: 1,
          isAvailable: true,
          canAfford: true,
        },
      ];
    }
    return [];
  }, [preview]);

  // Keep the player's choice while it can still pay; otherwise the first currency that can.
  useEffect(() => {
    setSelectedPaymentType((previous) => {
      if (
        previous !== null &&
        paymentOptions.some(
          (option) => option.paymentType === previous && isPayable(option)
        )
      ) {
        return previous;
      }
      return paymentOptions.find(isPayable)?.paymentType ?? null;
    });
  }, [paymentOptions]);

  const selectedOption = paymentOptions.find(
    (option) => option.paymentType === selectedPaymentType
  );

  const discountPercent = preview?.discountPercent ?? 0;
  const promotion = discountPercent > 0 ? preview?.promotion : undefined;
  const baseCost = preview?.baseCostInCleg ?? preview?.finalCostInCleg;
  const promotionRules = useMemo(() => {
    const running = preview?.availablePromotions ?? [];
    const shown = promotion
      ? running.filter((item) => item.id === promotion.id)
      : running.slice(0, 1);
    return (shown[0]?.rules ?? [])
      .filter((rule) => rule.conditionType === TROOP_COUNT_CONDITION)
      .sort((a, b) => a.conditionValue - b.conditionValue);
  }, [preview?.availablePromotions, promotion]);
  const shownPromotion =
    promotion ??
    (promotionRules.length ? preview?.availablePromotions?.[0] : undefined);
  const biggerDiscountAhead = promotionRules.some(
    (rule) => rule.discountPercent > discountPercent
  );

  const targetLabel =
    targetType === "all"
      ? t("barracks:dialogs.instantHeal.allTroops")
      : activeTroop?.troop?.name ||
        t("barracks:dialogs.instantHeal.troopNumber", { id: troopId });

  const selectedPercent = Math.min(
    100,
    Math.max(0, preview?.selectedPercentage ?? percentage)
  );

  const resetState = () => {
    setPercentage(100);
    setPreview(null);
    setSelectedPaymentType(null);
    setPreviewLoading(false);
    setSubmitLoading(false);
    setTroopsSkeletonCount(3);
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    setPercentage(100);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!percentageOptions.includes(percentage)) return;
    if (typeof troopId !== "number") return;

    let isActive = true;

    const fetchPreview = async () => {
      setPreviewLoading(true);
      const body = {
        percentage,
        ...(targetType === "single" && troopId > 0 ? { troopId } : {}),
      };
      const response = await axios.CallApi<InstantHealPreview>({
        url: "/playertroop/instant-heal/cost",
        method: "post",
        body,
      });
      if (!isActive) return;
      if (response.ok) {
        setPreview(response.data);
        if (typeof response.data?.troopsToHeal?.length === "number") {
          setTroopsSkeletonCount(response.data.troopsToHeal.length);
        }
      } else {
        setPreview(null);
        openToast(response);
      }
      setPreviewLoading(false);
    };

    fetchPreview();

    return () => {
      isActive = false;
    };
  }, [axios, open, percentage, troopId, targetType, refreshKey]);

  const handleClose = () => {
    barracksUIProps?.setInstantHealDialog?.((prev) => ({
      ...prev,
      open: false,
    }));
    resetState();
  };

  const handleConfirm = async () => {
    if (!selectedOption) return;
    setSubmitLoading(true);
    // The server prices the heal again. The promotion it was shown is sent so that, if it has
    // ended since, the heal is refused rather than charged at another price.
    const body = {
      percentage,
      ...(targetType === "single" && troopId > 0 ? { troopId } : {}),
      paymentType: selectedOption.paymentType,
      ...(promotion ? { promotionId: promotion.id } : {}),
    };
    const response = await axios.CallApi({
      url: "/playertroop/instant-heal",
      method: "post",
      body,
    });
    if (response.ok) {
      openToast({
        ok: true,
        message: t("barracks:dialogs.instantHeal.success"),
      });
      dispatch(fetchPlayerTroop(50));
      dispatch(fetchGetMe());
      handleClose();
    } else {
      openToast(response);
      // The price, the promotion or the balance may have changed: show the current ones.
      setRefreshKey((key) => key + 1);
    }
    setSubmitLoading(false);
  };

  const disableConfirm =
    !preview ||
    previewLoading ||
    submitLoading ||
    !selectedOption ||
    !preview?.troopsToHeal?.length;

  return (
    <Dialog
      open={open}
      size="lg"
      title={t("barracks:labels.instantHeal")}
      submitLabel={t("common:actions.confirm")}
      closeLabel={t("common:actions.close")}
      loading={submitLoading}
      disableSubmit={disableConfirm}
      onClose={handleClose}
      onSubmit={handleConfirm}
    >
      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <div className="bg-3 rounded p-3">
            <div className="row g-2 ">
              <div className="col-12 col-md-6 d-flex h-100 flex-column justify-content-between align-items-between">
                <div className="d-flex align-items-center gap-2">
                  {activeTroop?.troop?.image && targetType === "single" && (
                    <img
                      src={activeTroop?.troop?.image}
                      alt={activeTroop?.troop?.name}
                      width={44}
                      height={44}
                      style={{ borderRadius: 8, objectFit: "cover" }}
                    />
                  )}
                  <div>
                    <div className="text-muted small">{t("barracks:dialogs.instantHeal.target")}</div>
                    <div className="fw-bold">
                      {previewLoading
                        ? renderSkeletonLine(160)
                        : targetLabel}
                    </div>
                  </div>
                </div>
                <hr />
                <div>
                  <div className="small mt-3 mb-2 d-flex align-items-center gap-2">
                    <img
                      src={'/assets/images/icons/health.png'}
                      width={22}
                      height={22}
                      style={{ borderRadius: 8, objectFit: "cover" }}
                    />
                    {t("barracks:dialogs.instantHeal.healPercentage")}</div>
                  <div className="d-flex flex-wrap gap-2">
                    {percentageOptions.map((option) => (
                      <Button
                        key={option}
                        size="small"
                        variant={percentage === option ? "primary" : "default"}
                        onClick={() => setPercentage(option)}
                      >
                        {option}%
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-6 d-flex flex-column gap-2">
                <div className="bg-dark rounded p-2 instant-heal-summary">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="text-start">
                      <div className="text-muted small">{t("barracks:dialogs.instantHeal.selectedPercent")}</div>
                      <div className="fw-bold fs-5">
                        {previewLoading
                          ? renderSkeletonLine(50)
                          : `${selectedPercent}%`}
                      </div>
                      <div className="text-muted small">{t("barracks:dialogs.instantHeal.lostHp")}</div>
                    </div>
                    <div className="instant-heal-meter" aria-hidden="true">
                      <div
                        className="instant-heal-meter__fill"
                        style={{ height: `${selectedPercent}%` }}
                      >
                        <div className="instant-heal-meter__wave" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-dark rounded p-2 text-center">
                  <div className="text-muted small">{t("barracks:dialogs.instantHeal.hpToRestore")}</div>
                  <div className="fw-bold fs-5">
                    {previewLoading
                      ? renderSkeletonLine(90)
                      : formatValue(preview?.requiredHpAmount)}
                  </div>
                </div>
              </div>

            </div>
            <div className="border-top-1 my-3" />

            <div className="mt-3 bg-3 rounded p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className=" small">{t("barracks:dialogs.instantHeal.troopsToHeal")}</div>
                <div className="small">
                  {preview?.troopsToHeal?.length ?? 0}
                </div>
              </div>
              <div
                className="d-flex flex-column gap-2 mt-2"
                style={{ maxHeight: 190, overflowY: "auto" }}
              >
                {previewLoading &&
                  Array.from({ length: troopsSkeletonCount || 0 }).map(
                    (_, index) => (
                      <div
                        key={`troop-skeleton-${index}`}
                        className="bg-dark px-2 py-2 rounded"
                      >
                        {renderSkeletonBlock(24)}
                      </div>
                    ))}
                {!previewLoading && preview?.troopsToHeal?.length ? (
                  preview.troopsToHeal.map((entry, index) => {
                    const entryId = getTroopEntryId(entry);
                    const localTroop = playerTroopList?.find(
                      (troop) => troop.id === entryId
                    );
                    const name =
                      entry?.name ||
                      entry?.troopName ||
                      localTroop?.troop?.name ||
                      t("barracks:dialogs.instantHeal.troopNumber", {
                        id: entryId || index + 1,
                      });
                    const image =
                      entry?.image ||
                      entry?.icon ||
                      localTroop?.troop?.image ||
                      localTroop?.troop?.icon;
                    const health =
                      entry?.health ??
                      entry?.currentHealth ??
                      localTroop?.health;
                    const maxHealth = entry?.maxHealth ?? localTroop?.maxHealth;

                    return (
                      <div
                        key={`${entryId}-${index}`}
                        className="d-flex align-items-center justify-content-between bg-dark px-2 py-2 rounded"
                      >
                        <div className="d-flex align-items-center gap-2">
                          {image && (
                            <img
                              src={image}
                              alt={name}
                              width={32}
                              height={32}
                              style={{ borderRadius: 6, objectFit: "cover" }}
                            />
                          )}
                          <div className="text-white small">{name}</div>
                        </div>
                        {health !== undefined && maxHealth !== undefined && (
                          <div className="text-muted small">
                            {formatValue(health)}/{formatValue(maxHealth)}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : !previewLoading ? (
                  <div className="text-muted small">{t("barracks:dialogs.instantHeal.noTroopsListed")}</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="bg-3 rounded p-3 h-100 d-flex flex-column">
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-muted small">{t("barracks:dialogs.instantHeal.costPreview")}</div>
              {previewLoading && (
                <div className="text-muted small">{t("common:labels.loading")}</div>
              )}
            </div>
            {!previewLoading && shownPromotion && (
              <div className="instant-heal-promo mt-2">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span className="fw-bold instant-heal-promo__name">
                    {shownPromotion.name}
                  </span>
                  {promotion && (
                    <Badge bg="danger">
                      {t("barracks:dialogs.instantHeal.discountOff", {
                        percent: roundFloat(discountPercent),
                      })}
                    </Badge>
                  )}
                </div>
                {shownPromotion.description && (
                  <div className="small text-muted mt-1">
                    {shownPromotion.description}
                  </div>
                )}
                {promotionRules.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {promotionRules.map((rule) => (
                      <span
                        key={rule.id}
                        className={`instant-heal-promo__rule${
                          promotion?.ruleId === rule.id ? " is-applied" : ""
                        }`}
                      >
                        {t("barracks:dialogs.instantHeal.ruleTroops", {
                          count: rule.conditionValue,
                        })}{" "}
                        · {roundFloat(rule.discountPercent)}%
                      </span>
                    ))}
                  </div>
                )}
                {biggerDiscountAhead && (
                  <div className="small text-muted mt-1">
                    {t("barracks:dialogs.instantHeal.promotionTip")}
                  </div>
                )}
              </div>
            )}

            <div className="d-flex flex-column gap-1 mt-2">
              {previewLoading ? (
                renderSkeletonBlock(34)
              ) : (
                <>
                  {promotion && (
                    <div className="d-flex align-items-center justify-content-between small text-muted">
                      <span>{t("barracks:dialogs.instantHeal.baseCost")}</span>
                      <del>
                        {formatCost(baseCost)} CLEG
                      </del>
                    </div>
                  )}
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    {promotion && (
                      <span className="small text-muted">
                        {t("barracks:dialogs.instantHeal.youPay")}
                      </span>
                    )}
                    <Badge className="d-flex align-items-center" bg="primary">
                      <img
                        src={clegIcon}
                        className="me-2"
                        width={22}
                        height={22}
                        alt="CLEG"
                      />
                      {formatCost(preview?.finalCostInCleg)} CLEG
                    </Badge>
                  </div>
                </>
              )}
            </div>

            <div className="border-top-1 my-3" />

            <div>
              <div className="text-muted small mb-2">{t("barracks:dialogs.instantHeal.paymentOptions")}</div>
              {!previewLoading && paymentOptions.length === 0 && (
                <div className="text-muted small">
                  {t("barracks:dialogs.instantHeal.noPaymentOptions")}
                </div>
              )}
              <div className="row g-2">
                {previewLoading &&
                  Array.from({ length: 3 }).map((_, index) => (
                    <div className="col-12 col-sm-6 col-lg-12" key={index}>
                      {renderSkeletonBlock(32)}
                    </div>
                  ))}
                {!previewLoading &&
                  paymentOptions.map((option) => {
                    const currency = currencies[option.paymentType];
                    const label = t(
                      `barracks:dialogs.instantHeal.currencies.${currency.key}`
                    );
                    const isSelected = selectedPaymentType === option.paymentType;
                    const reason = !option.isAvailable
                      ? t("barracks:dialogs.instantHeal.noMarketPrice")
                      : !option.canAfford
                      ? t("barracks:dialogs.instantHeal.notEnough", {
                          currency: label,
                        })
                      : null;
                    return (
                      <div
                        className="col-12 col-sm-6 col-lg-12"
                        key={option.paymentType}
                      >
                        <Button
                          size="small"
                          variant={isSelected ? "wide-primary" : "wide"}
                          onClick={() =>
                            isPayable(option) &&
                            setSelectedPaymentType(option.paymentType)
                          }
                          disabled={!isPayable(option)}
                          className="w-100 justify-content-start px-3 b-100"
                        >
                          <img
                            src={currency.icon}
                            alt={label}
                            width={18}
                            height={18}
                            className="me-2"
                          />
                          {option.isAvailable ? formatCost(option.amount) : "--"}{" "}
                          {label}
                        </Button>
                        <div className="d-flex flex-wrap justify-content-between gap-1 small text-muted px-1 mt-1">
                          {typeof option.balance === "number" && (
                            <span>
                              {t("barracks:dialogs.instantHeal.balance", {
                                amount: formatValue(option.balance),
                              })}
                            </span>
                          )}
                          {reason ? (
                            <span className="text-danger">{reason}</span>
                          ) : (
                            option.paymentType !== CLEG_PAYMENT && (
                              <span>
                                {t("barracks:dialogs.instantHeal.rate", {
                                  currency: label,
                                  price: roundFloat(option.priceInCleg, 4),
                                })}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>


    </Dialog>
  );
};

export default InstantHealDialog;
